import React, { useRef, useEffect, forwardRef } from 'react';
import '../../../shared/columns/styles/PopupWindowInput.scss';
import {
    flatRateValue,
    balticIndexValue,
    sparkIndexValue,
} from '../models/RateTypes';
import {
    createFlatRatePart,
    createBalticIndexPart,
    createSparkIndexPart,
    createSeparatorRatePart,
} from '../models/RatePart';
import {
    KEY_BACKSPACE,
    KEY_TAB,
    KEY_ENTER,
    KEY_LEFT,
    KEY_RIGHT,
    KEY_PLUS,
    KEY_FORWARD_SLASH,
    KEY_EQUAL,
    KEY_C,
    KEY_I,
    KEY_O,
    KEY_R,
} from '../../../../constants/keyboardCodes';
import {
    SEPARATOR_PLUS,
    SEPARATOR_FORWARD_SLASH,
    ENTITY_PART_TYPE_RATE,
} from '../../../../models/common/EntityPart';
import { changeFocusOnKeyDown } from '_legacy/services/TrapFocusService';
import { useRateStore } from '../store/RateStore';
import Duration from './Duration';
import RateType from './RateType';
import Currency from './Currency';
import Bound from './Bound';
import RateIndex from './RateIndex';
import Unit from './Unit';
import Amount from './Amount';
import { convertRateToInputStateModel } from '../tools/RateConverter';
import NetRate from './NetRate';
import {
    validateDurationAmount,
    validateAmount,
} from '../services/ValidationService';
import { applyAutomaticBalticRouteSelection } from '../services/BalticRouteAutoSelector';
import { DATASET_LPG_VLGC } from '_legacy/models/Datasets';
import BalticRoute from './BalticRoute';
import EquivToggle from './EquivToggle';
import Info from './Info';
import {
    isUnitSupportedForType,
    rateNotReportedValue,
    rateOpenValue,
    coaValue,
    indexValue,
} from '../models/RateUnits';

const RateInput = forwardRef((props, ref) => {
    const containerId = 'rate-elements';

    const didMountRef = useRef(false);
    const amountInputRef = useRef(null);
    const durationInputRef = useRef(null);
    const unitInputRef = useRef(null);
    const store = useRateStore();

    useEffect(() => {
        if (didMountRef.current) {
            const { isEditMode, selectedIndex } = props;
            if (checkIfChangesAreFromUI(isEditMode, selectedIndex)) {
                return;
            }
            if (isEditMode === true && selectedIndex !== store.selectedIndex) {
                updateSelectedRate();
            } else {
                // Shows that we deselected selected rate, so, set state to default
                store.setInitialState(
                    props.data?.type,
                    props.data?.tankersGroupType
                );
                if (
                    props.context.datasetId === DATASET_LPG_VLGC &&
                    props.data &&
                    props.context.isBalticInfoExtendedMode
                ) {
                    applyAutomaticBalticRouteSelection(props.data, store);
                }
                focusAmount();
            }
        } else {
            didMountRef.current = true;
            store.setInitialState(
                props.data?.type,
                props.data?.tankersGroupType
            );
            if (
                props.context.datasetId === DATASET_LPG_VLGC &&
                props.data &&
                props.context.isBalticInfoExtendedMode
            ) {
                applyAutomaticBalticRouteSelection(props.data, store);
            }
            focusAmount();
        }
    });

    const handleInitialChar = (initialChar) => {
        if (!initialChar) {
            return;
        }

        if (initialChar.toLowerCase() === 'c') {
            handleInputKeyDown({ keyCode: KEY_C });
            return;
        }

        if (initialChar.toLowerCase() === 'i') {
            handleInputKeyDown({ keyCode: KEY_I });
            return;
        }

        if (initialChar.toLowerCase() === 'r') {
            handleInputKeyDown({ keyCode: KEY_R });
            return;
        }

        if (initialChar.toLowerCase() === 'o') {
            handleInputKeyDown({ keyCode: KEY_O });
            return;
        }

        if (amountInputHasFocus()) {
            store.setAmount(initialChar);
        }
    };

    const checkIfChangesAreFromUI = (isEditMode, selectedIndex) => {
        return (
            isEditMode === store.isEditMode &&
            selectedIndex === store.selectedIndex
        );
    };

    const focusAmount = () => {
        if (amountInputRef.current) {
            setTimeout(() => {
                amountInputRef.current.focus();
                handleInitialChar(props.initialChar);
            }, 0);
        }
    };

    const focusUnit = () => {
        if (unitInputRef.current) {
            setTimeout(() => {
                unitInputRef.current.focus();
            }, 0);
        }
    };

    const amountInputHasFocus = () => {
        return amountInputRef.current === document.activeElement;
    };

    const durationAmountInputHasFocus = () => {
        return durationInputRef.current === document.activeElement;
    };

    const handleInputKeyDown = (e) => {
        switch (e.keyCode) {
            case KEY_BACKSPACE:
                break;
            case KEY_TAB:
                if (props.onTab && props.onTabBack) {
                    e.preventDefault();
                    e.stopPropagation();
                    handleOnAddClick(
                        e,
                        !e.shiftKey ? props.onTab : props.onTabBack
                    );
                }
                break;
            case KEY_LEFT:
            case KEY_RIGHT:
                changeFocusOnKeyDown(containerId, e);
                break;
            case KEY_ENTER:
                if (amountInputHasFocus() || durationAmountInputHasFocus()) {
                    handleOnAddClick(e);
                }
                break;
            case KEY_EQUAL:
                if (e.shiftKey) {
                    handleOnAddSeparator(SEPARATOR_PLUS);
                }
                break;
            case KEY_PLUS:
                handleOnAddSeparator(SEPARATOR_PLUS);
                break;
            case KEY_FORWARD_SLASH:
                handleOnAddSeparator(SEPARATOR_FORWARD_SLASH);
                break;
            case KEY_C:
                addRateViaShortcut(coaValue);
                break;
            case KEY_I:
                addRateViaShortcut(indexValue);
                break;
            case KEY_O:
                addRateViaShortcut(rateOpenValue);
                break;
            case KEY_R:
                addRateViaShortcut(rateNotReportedValue);
                break;
            default:
        }
    };

    const handleOnAddClick = (e, tabOutAction) => {
        const validationResult = validationOnSubmit();
        store.onSubmit(validationResult);

        if (
            store.isAmountValid &&
            validationResult.isAmountValid &&
            (checkIsFlatRateMode() || validationResult.isIndexValid) &&
            (!props.context.isExtendedMode ||
                (validationResult.isDurationAmountValid &&
                    validationResult.arePartsValid))
        ) {
            const part =
                store.type.value === flatRateValue
                    ? createFlatRatePart({
                          currency: store.currency && store.currency.value,
                          unit: store.unit.value,
                          amountFrom: store.amountObj && store.amountObj.from,
                          amountTo: store.amountObj && store.amountObj.to,
                          bound: store.bound && store.bound.value,
                          durationAmountFrom:
                              store.durationAmountObj &&
                              store.durationAmountObj.from,
                          durationAmountTo:
                              store.durationAmountObj &&
                              store.durationAmountObj.to,
                          durationUnit: store.durationUnit.value,
                          balticRoute:
                              store.balticRoute && store.balticRoute.value,
                          info: store.info && store.info.value,
                          isEquiv: store.isEquiv,
                      })
                    : store.type.value === balticIndexValue
                    ? createBalticIndexPart({
                          amountFrom: store.amountObj && store.amountObj.from,
                          amountTo: store.amountObj && store.amountObj.to,
                          index: store.index && store.index.value,
                          durationAmountFrom:
                              store.durationAmountObj &&
                              store.durationAmountObj.from,
                          durationAmountTo:
                              store.durationAmountObj &&
                              store.durationAmountObj.to,
                          durationUnit: store.durationUnit.value,
                      })
                    : createSparkIndexPart({
                          amountFrom: store.amountObj && store.amountObj.from,
                          amountTo: store.amountObj && store.amountObj.to,
                          index: store.index && store.index.value,
                          durationAmountFrom:
                              store.durationAmountObj &&
                              store.durationAmountObj.from,
                          durationAmountTo:
                              store.durationAmountObj &&
                              store.durationAmountObj.to,
                          durationUnit: store.durationUnit.value,
                      });

            props.onAddValue(part, tabOutAction);
            store.setInitialState(
                props.data?.type,
                props.data?.tankersGroupType
            );
            focusAmount();

            return;
        }

        tabOutAction && tabOutAction();
    };

    const validationOnSubmit = () => {
        const {
            amountObj,
            isAmountDisabled,
            index,
            durationUnit,
            durationAmountObj,
            durationAmount,
            isEditMode,
            selectedIndex,
        } = store;

        const isAmountValid = isAmountDisabled
            ? true
            : validateAmount(amountObj);
        const isIndexValid = index ? true : false;
        const { isDurationAmountValid, durationAmountValidationErrorMessage } =
            validateDurationAmount(
                durationAmount,
                durationAmountObj,
                durationUnit
            );

        const arePartsValid =
            props.context.isExtendedMode && durationAmount !== ''
                ? props.partsValidation({
                      parts: props.parts,
                      partType: ENTITY_PART_TYPE_RATE,
                      isEditMode: isEditMode,
                      selectedIndex: selectedIndex,
                      duration: {
                          amount: durationAmountObj,
                          unit: durationUnit.value,
                      },
                  })
                : true;

        return {
            isAmountValid,
            isIndexValid,
            isDurationAmountValid,
            arePartsValid,
            durationAmountValidationErrorMessage,
        };
    };

    const handleOnAddSeparator = (character) => {
        const separatorPart = createSeparatorRatePart(character);
        store.setInitialState(props.data?.type, props.data?.tankersGroupType);
        props.onAddSeparator(separatorPart);
    };

    const addRateViaShortcut = (rateUnit) => {
        if (store.isUnitSelectedByUser) {
            return;
        }

        const hasRateAlready = props.parts.some(
            (part) => part.rateValue?.flatRate?.unit === rateUnit
        );

        if (
            hasRateAlready ||
            !isUnitSupportedForType(
                rateUnit,
                props.data.type,
                props.data?.tankersGroupType,
                props.data?.datasetId
            )
        ) {
            return;
        }

        const part = createFlatRatePart({
            currency: null,
            unit: rateUnit,
            amountFrom: null,
            amountTo: null,
            bound: null,
            durationAmountFrom: null,
            durationAmountTo: null,
            durationUnit: null,
            balticRoute: null,
            info: null,
            isEquiv: false,
        });

        props.onAddValue(part);
        store.setInitialState(props.data?.type, props.data?.tankersGroupType);
    };

    const checkIsFlatRateMode = () => {
        return store.type.value === flatRateValue;
    };

    const checkRatePartIsValid = () => {
        return checkIsFlatRateMode()
            ? store.isAmountValid
            : store.isAmountValid && store.index;
    };

    const updateSelectedRate = () => {
        const { selectedValue: selectedRate } = props;
        const newState = convertRateToInputStateModel(
            selectedRate,
            props.data.type,
            props.context.datasetId,
            props.selectedIndex,
            props.data.tankersGroupType
        );
        store.setCustomState(newState);
        store.setIsUnitSelectedByUser(true);
        newState.unit.shouldDisableAmount ? focusUnit() : focusAmount();
    };

    return (
        <div
            id={containerId}
            style={{ minWidth: 'auto' }}
            onKeyDown={handleInputKeyDown}
            onFocus={props.resetSeparatorValidation}
        >
            {props.context.isExtendedMode && (
                <RateType
                    datasetId={props.context.datasetId}
                    amountInputRef={amountInputRef}
                />
            )}
            <div
                className={
                    props.context.isExtendedMode
                        ? 'inline vertical-divider'
                        : 'first-inline-item'
                }
            >
                <div
                    className="inline-title"
                    style={{ display: 'flex', justifyContent: 'space-between' }}
                >
                    <div style={{ float: 'left' }}>Rate values </div>
                    <NetRate {...props} />
                </div>

                {checkIsFlatRateMode() && <Bound {...props} />}
                {checkIsFlatRateMode() && <Currency />}
                <Amount {...props} amountInputRef={amountInputRef} />
                {(store.type.value === balticIndexValue ||
                    store.type.value === sparkIndexValue) && (
                    <RateIndex
                        {...props}
                        isSparkIndex={store.type.value === sparkIndexValue}
                    />
                )}
                {checkIsFlatRateMode() && (
                    <Unit {...props} unitInputRef={unitInputRef} />
                )}
            </div>
            {checkIsFlatRateMode() &&
                props.context.datasetId === DATASET_LPG_VLGC &&
                props.context.isBalticInfoExtendedMode &&
                store.unit?.value === 'PMT' && (
                    <div
                        className="first-inline-item"
                        style={{
                            marginLeft: 0,
                        }}
                    >
                        <div
                            className="inline-title"
                            style={{
                                alignContent: 'flex-end',
                            }}
                        >
                            <EquivToggle />
                        </div>

                        <BalticRoute />
                        <Info />
                    </div>
                )}
            {props.context.isExtendedMode && (
                <Duration {...props} durationInputRef={durationInputRef} />
            )}
            <div className="inline" style={{ marginTop: '20px' }}>
                <div className="inline capture-button" title="Capture Rate">
                    <button
                        name="add-rate"
                        className="ui primary button"
                        onClick={handleOnAddClick}
                        disabled={!checkRatePartIsValid()}
                    >
                        +
                    </button>
                </div>
            </div>
        </div>
    );
});

export default RateInput;
